// ------------------------------------------------------------------
// Presell VARIANT: RAMBO
// DNA: soft rounded floating cards + shadows, generous whitespace, airy
// "friendly SaaS". Red-circle numbered timeline with dotted connector.
// Pink/red-tinted highlight on the Haul Star pricing column. Pill buttons.
// ------------------------------------------------------------------

import React from 'react';
import { func } from 'prop-types';

import heroImg from '../../assets/presell/hero-large.webp';
import dumpImg from '../../assets/presell/dump-trailer.jpg';
import carHaulerImg from '../../assets/presell/car-hauler.jpg';
import logoInverted from '../../assets/presell/logo-clean-inverted.png';

import {
  CHANNELS,
  CONTROL,
  STEPS,
  TABLE_ROWS,
  HS_OFFER,
  PAINS,
  TOOLBOX,
  STATS,
  FAQ,
  COPY,
} from './presellContent';
import css from './RamboLayout.module.css';

const RamboLayout = ({ listFreeLink, haulStarLink, newsletterLink }) => (
  <div className={css.root}>
    {/* HERO */}
    <section className={css.hero}>
      <div className={css.wrap}>
        <div className={css.heroGrid}>
          <div className={css.heroCopy}>
            <p className={css.eyebrow}>{COPY.eyebrowHero}</p>
            <h1 className={css.h1}>
              {COPY.h1a}
              <span className={css.red}>{COPY.h1red}</span>
            </h1>
            <p className={css.lede}>{COPY.lede}</p>
            <div className={css.btns}>
              {listFreeLink(
                `${css.btn} ${css.btnPrimary} ${css.btnLg} cta-list-free`,
                'hero',
                COPY.ctaListFree
              )}
              <a href="#how" className={`${css.btn} ${css.btnOutline} ${css.btnLg}`}>
                {COPY.ctaHowItWorks}
              </a>
            </div>
            <p className={css.microtrust}>{COPY.microtrust}</p>
          </div>
          <div className={css.heroPhoto}>
            <img src={heroImg} alt="Trailers ready to rent on BigLoads" loading="eager" />
            <div className={css.heroBadge}>
              <span className={css.heroBadgeDot}>📍</span> {COPY.heroBadge}
            </div>
          </div>
        </div>
      </div>
    </section>

    {/* CHANNELS */}
    <section className={css.sec}>
      <div className={`${css.wrap} ${css.center}`}>
        <h2 className={css.h2}>
          Don&rsquo;t replace your marketing. <span className={css.mark}>{COPY.channelsH}</span>
        </h2>
        <p className={css.subCenter}>{COPY.channelsSub}</p>
        <div className={css.chanRow}>
          {CHANNELS.map(c => (
            <div key={c.h} className={css.chan}>
              <div className={css.chanIc}>{c.ic}</div>
              <h4 className={css.chanH}>{c.h}</h4>
              <p className={css.chanP}>{c.p}</p>
            </div>
          ))}
          <div className={css.chanPlus}>+</div>
          <div className={css.chanBl}>
            <img src={logoInverted} alt="BigLoads" className={css.chanBlLogo} />
            <p>{COPY.channelsBl}</p>
          </div>
        </div>
        <p className={css.chanNote}>{COPY.channelsNote}</p>
      </div>
    </section>

    {/* CONTROL */}
    <section className={`${css.sec} ${css.control}`}>
      <div className={`${css.wrap} ${css.center}`}>
        <h2 className={css.h2}>
          {COPY.controlH1}
          <span className={css.red}>{COPY.controlH2red}</span>
        </h2>
        <div className={css.ctrlGrid}>
          {CONTROL.map(c => (
            <div key={c.h} className={css.ctrlCard}>
              <div className={css.ctrlIc}>{c.ic}</div>
              <h3 className={css.h3}>{c.h}</h3>
              <p>{c.p}</p>
            </div>
          ))}
        </div>
        <p className={css.controlNote}>{COPY.controlNote}</p>
      </div>
    </section>

    {/* HOW IT WORKS — red circles + dotted connector */}
    <section className={css.sec} id="how">
      <div className={`${css.wrap} ${css.center}`}>
        <p className={css.eyebrow}>{COPY.howEyebrow}</p>
        <h2 className={css.h2}>{COPY.howH}</h2>
        <div className={css.steps}>
          {STEPS.map(s => (
            <div key={s.n} className={css.step}>
              <div className={css.stepN}>{s.n}</div>
              <h3 className={css.h3}>{s.h}</h3>
              <p>{s.p}</p>
            </div>
          ))}
        </div>
        <div className={css.nocut}>
          <span className={css.nocutIcn}>🚫</span>
          <p>{COPY.noCut}</p>
        </div>
      </div>
    </section>

    {/* PRICING — centered pink-highlight table */}
    <section className={`${css.sec} ${css.pricing}`} id="pricing">
      <div className={`${css.wrap} ${css.center}`}>
        <p className={css.eyebrow}>{COPY.pricingEyebrow}</p>
        <h2 className={css.h2}>{COPY.pricingH}</h2>
        <div className={css.tblWrap}>
          <table className={css.table}>
            <thead>
              <tr>
                <th>Feature</th>
                <th className={css.colFree}>Free Listing</th>
                <th className={css.colHs}>
                  Haul Star <span className={css.hsTag}>POPULAR</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {TABLE_ROWS.map(r => (
                <tr key={r.f}>
                  <td>{r.f}</td>
                  <td className={css.colFree}>
                    {r.free === true ? (
                      <span className={css.yes}>✓</span>
                    ) : r.free === false ? (
                      <span className={css.no}>—</span>
                    ) : (
                      r.free
                    )}
                  </td>
                  <td className={css.colHs}>
                    {r.hs === true ? <span className={css.yes}>✓</span> : r.hs}
                  </td>
                </tr>
              ))}
              <tr className={css.priceRow}>
                <td>Price</td>
                <td className={css.colFree}>$0</td>
                <td className={`${css.colHs} ${css.priceHs}`}>$30/mo</td>
              </tr>
            </tbody>
          </table>
        </div>
        <p className={css.tblNote}>{COPY.pricingTableNote}</p>
        <div className={css.btns} style={{ marginTop: 26 }}>
          {listFreeLink(
            `${css.btn} ${css.btnPrimary} ${css.btnLg} cta-list-free`,
            'pricing',
            COPY.ctaListFree
          )}
          {haulStarLink(
            `${css.btn} ${css.btnOutline} ${css.btnLg} cta-haul-star`,
            'pricing',
            COPY.ctaHaulStarStart
          )}
        </div>
      </div>
    </section>

    {/* VALUE FRAMING */}
    <section className={css.sec}>
      <div className={css.wrap}>
        <div className={css.valueGrid}>
          <div>
            <p className={css.eyebrow}>{COPY.valueEyebrow}</p>
            <h2 className={css.h2}>{COPY.valueH}</h2>
            <p className={css.sub}>{COPY.valueSub}</p>
            <div className={css.proofBox}>
              <div className={css.proofBig}>
                {COPY.proofBigA}
                <span className={css.red}>{COPY.proofBigRed}</span>
                {COPY.proofBigB}
              </div>
              <p className={css.proofSmall}>{COPY.proofSmall}</p>
            </div>
            <p className={css.valuePunch}>{COPY.valuePunch}</p>
          </div>
          <div className={css.valueBox}>
            <p className={css.valueBoxLabel}>Haul Star</p>
            <div className={css.valuePrice}>
              $30<small>/month</small>
            </div>
            <ul className={css.valueList}>
              {COPY.valueBoxList.map(v => (
                <li key={v}>{v}</li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>

    {/* FACEBOOK */}
    <section className={`${css.sec} ${css.fb}`}>
      <div className={css.wrap}>
        <div className={css.fbGrid}>
          <div>
            <p className={css.eyebrow}>{COPY.fbEyebrow}</p>
            <h2 className={css.h2}>{COPY.fbH}</h2>
            <p className={css.sub}>{COPY.fbSub}</p>
            <p className={css.fbPunch}>{COPY.fbPunch}</p>
          </div>
          <div className={css.listingCard}>
            <div className={css.lcPhoto}>
              <img src={dumpImg} alt="7x14 Dump Trailer listing" loading="lazy" />
            </div>
            <div className={css.lcBody}>
              <h4 className={css.lcH}>{COPY.listingCardH}</h4>
              <p className={css.lcMeta}>{COPY.listingCardMeta}</p>
              <div className={css.lcCall}>📞 Call Owner</div>
            </div>
          </div>
        </div>
      </div>
    </section>

    {/* SEARCH INTENT */}
    <section className={css.sec}>
      <div className={`${css.wrap} ${css.center}`}>
        <p className={css.eyebrow}>{COPY.searchEyebrow}</p>
        <h2 className={css.h2}>{COPY.searchH}</h2>
        <div className={css.searchBarWrap}>
          <span className={css.searchBar}>
            <span className={css.mag}>🔍</span> {COPY.searchQuery}
          </span>
        </div>
        <div className={css.flow}>
          <div className={css.flowStep}>
            <span className={css.fsLbl}>They search</span>&quot;{COPY.searchQuery}&quot;
          </div>
          <span className={css.flowArrow}>→</span>
          <div className={css.flowStep}>
            <span className={css.fsLbl}>They see</span>Your BigLoads result
          </div>
          <span className={css.flowArrow}>→</span>
          <div className={css.flowStep}>
            <span className={css.fsLbl}>They open</span>Your local trailer listing
          </div>
          <span className={css.flowArrow}>→</span>
          <div className={css.flowStep}>
            <span className={css.fsLbl}>They act</span>📞 Call Owner
          </div>
        </div>
      </div>
    </section>

    {/* PAINS */}
    <section className={css.sec}>
      <div className={`${css.wrap} ${css.center}`}>
        <p className={css.eyebrow}>{COPY.painsEyebrow}</p>
        <h2 className={css.h2}>{COPY.painsH}</h2>
        <div className={css.painsGrid}>
          {PAINS.map(p => (
            <div key={p.s} className={css.pain}>
              <div className={css.painIc}>{p.ic}</div>
              <span>{p.s}</span>
            </div>
          ))}
        </div>
        <p className={css.painsNote}>{COPY.painsNote}</p>
      </div>
    </section>

    {/* RESOURCES */}
    <section className={`${css.sec} ${css.res}`} id="resources">
      <p className={`${css.eyebrow} ${css.center}`}>{COPY.resEyebrow}</p>
      <h2 className={`${css.h2} ${css.center}`}>{COPY.resH}</h2>
      <div className={css.wrap}>
        <div className={css.resGrid}>
          <div>
            <h3 className={css.h3}>{COPY.resGuidesH}</h3>
            <p className={css.resIntro}>{COPY.resGuidesIntro}</p>
            <div className={css.guides}>
              <div className={css.guide}>
                <div className={css.gPhoto}>
                  <img src={carHaulerImg} alt="Car hauler loading guide" loading="lazy" />
                </div>
                <div className={css.gLbl}>Car Hauler</div>
              </div>
              <div className={css.guide}>
                <div className={css.gPhoto}>
                  <img src={dumpImg} alt="Dump trailer loading guide" loading="lazy" />
                </div>
                <div className={css.gLbl}>Dump Trailer</div>
              </div>
              <div className={css.guide}>
                <div className={css.gPhoto}>
                  <img src={carHaulerImg} alt="Equipment trailer loading guide" loading="lazy" />
                </div>
                <div className={css.gLbl}>Equipment</div>
              </div>
            </div>
          </div>
          <div>
            <h3 className={css.h3}>{COPY.resToolboxH}</h3>
            <ul className={css.resList}>
              {TOOLBOX.map(t => (
                <li key={t}>{t}</li>
              ))}
            </ul>
            <div className={css.newsletterCard}>
              <div className={css.newsletterH}>{COPY.newsletterH}</div>
              <p className={css.newsletterP}>{COPY.newsletterP}</p>
              {newsletterLink(
                `${css.btn} ${css.btnPrimary} ${css.btnSm}`,
                'resources',
                COPY.newsletterCta
              )}
            </div>
          </div>
        </div>
      </div>
    </section>

    {/* DASHBOARD */}
    <section className={css.sec}>
      <div className={`${css.wrap} ${css.center}`}>
        <p className={css.eyebrow}>{COPY.dashEyebrow}</p>
        <h2 className={css.h2}>{COPY.dashH}</h2>
        <p className={css.subCenter}>{COPY.dashSub}</p>
        <div className={css.dashStats}>
          {STATS.map(s => (
            <div key={s.lbl} className={css.stat}>
              <div className={css.statIc}>{s.ic}</div>
              <div className={css.statNum}>{s.num}</div>
              <div className={css.statLbl}>{s.lbl}</div>
            </div>
          ))}
        </div>
        <div className={css.dashCta}>
          {listFreeLink(`${css.dashCtaLink} cta-list-free`, 'dashboard', COPY.dashCta)}
        </div>
      </div>
    </section>

    {/* FAQ */}
    <section className={`${css.sec} ${css.pricing}`} id="faq">
      <div className={`${css.wrap} ${css.center}`}>
        <p className={css.eyebrow}>{COPY.faqEyebrow}</p>
        <h2 className={css.h2}>{COPY.faqH}</h2>
        <div className={css.faq}>
          {FAQ.map(f => (
            <details key={f.q} className={css.faqItem}>
              <summary className={css.faqQ}>{f.q}</summary>
              <div className={css.faqA}>{f.a}</div>
            </details>
          ))}
        </div>
      </div>
    </section>

    {/* HAUL STAR CLOSE */}
    <section className={`${css.sec} ${css.closeBand}`} id="haulstar">
      <div className={css.wrap}>
        <div className={css.closeGrid}>
          <div className={css.closeCopy}>
            <p className={css.eyebrow}>{COPY.closeEyebrow}</p>
            <h2 className={css.h2}>{COPY.closeH}</h2>
            <p className={css.closeLede}>{COPY.closeLede}</p>
            <p className={css.closeHeadline}>{COPY.closeHeadline}</p>
            <div className={css.btns}>
              {haulStarLink(
                `${css.btn} ${css.btnPrimary} ${css.btnLg} cta-haul-star`,
                'close',
                COPY.ctaBecomeHaulStar
              )}
              {listFreeLink(
                `${css.btn} ${css.btnOutlineLight} ${css.btnLg} cta-list-free`,
                'close',
                COPY.ctaNotReady
              )}
            </div>
          </div>
          <div className={css.hsOffer}>
            <div className={css.hsTop}>
              <span className={css.hsTopT}>Haul Star</span>
              <span className={css.hsTopPr}>$30/mo</span>
            </div>
            <ul className={css.hsOfferList}>
              {HS_OFFER.map(o => (
                <li key={o}>{o}</li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>

    {/* FINAL CTA */}
    <section className={css.final}>
      <div className={css.wrap}>
        <h2 className={css.finalH}>{COPY.finalH}</h2>
        <p className={css.finalTagline}>{COPY.finalTagline}</p>
        <div className={`${css.btns} ${css.center}`}>
          {listFreeLink(
            `${css.btn} ${css.btnPrimary} ${css.btnLg} cta-list-free`,
            'final',
            COPY.ctaListFree
          )}
          {haulStarLink(
            `${css.btn} ${css.btnOutlineLight} ${css.btnLg} cta-haul-star`,
            'final',
            COPY.ctaHaulStarStart
          )}
        </div>
      </div>
    </section>
  </div>
);

RamboLayout.propTypes = {
  listFreeLink: func.isRequired,
  haulStarLink: func.isRequired,
  newsletterLink: func.isRequired,
};

export default RamboLayout;
